import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { settingsApi } from '../../services';
import api from '../../services/api';

interface SettingsState {
  settings: Record<string, any>;
  companyInfo: Record<string, any>;
  preferences: Record<string, any>;
  loading: boolean;
  error: any | null;
  /**
   * In-flight request count. A single `loading` boolean was cleared by
   * whichever thunk settled first, so overlapping requests (fetchSettings +
   * fetchPreferences, both dispatched on app boot) dropped the spinner while
   * the other was still running.
   */
  pendingCount: number;
}

const initialState: SettingsState = {
  settings: {},
  companyInfo: {},
  preferences: {},
  loading: false,
  error: null,
  pendingCount: 0,
};

/**
 * Unwraps the BaseController::sendResponse envelope ({ success, message,
 * data }) without ever storing the envelope itself in state: a falsy-but-
 * present `data` (null from sendResponse(null, msg)) yields `{}` so the
 * object-shaped slices keep their shape, while an unwrapped (raw) body is
 * passed through unchanged. `||` fallbacks silently replaced missing data
 * with the whole response body, corrupting the slice.
 */
const unwrapData = (body: any): any =>
  body && typeof body === 'object' && 'data' in body ? body.data ?? {} : body;

export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getAll();
      const payload = unwrapData(response.data);
      return payload?.settings ?? payload;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/updateSettings',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await api.put('/settings', data);
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchCompanyInfo = createAsyncThunk(
  'settings/fetchCompanyInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getCompany();
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateCompanyInfo = createAsyncThunk(
  'settings/updateCompanyInfo',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await settingsApi.updateCompany(data);
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchPreferences = createAsyncThunk(
  'settings/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/settings/preferences');
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updatePreferences = createAsyncThunk(
  'settings/updatePreferences',
  async (data: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await api.put('/settings/preferences', data);
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchSettingsData = createAsyncThunk(
  'settings/fetchSettingsData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.getSettingsData();
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateSettingsWithId = createAsyncThunk(
  'settings/updateSettingsWithId',
  async ({ id, data }: { id: number | string; data: Record<string, any> }, { rejectWithValue }) => {
    try {
      const response = await settingsApi.update(id, data);
      return unwrapData(response.data);
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const clearCache = createAsyncThunk(
  'settings/clearCache',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsApi.clearCache();
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    reset(state) {
      state.settings = {};
      state.companyInfo = {};
      state.preferences = {};
      state.loading = false;
      state.error = null;
      state.pendingCount = 0;
    },
    setSettingLocal(state, action: { payload: { key: string; value: any } }) {
      state.settings[action.payload.key] = action.payload.value;
    }
  },
  extraReducers: (builder) => {
    const handlePending = (state: SettingsState) => {
      state.pendingCount += 1;
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: SettingsState, action: any) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
      state.error = action.payload;
    };
    const handleFulfilled = (state: SettingsState) => {
      state.pendingCount = Math.max(0, state.pendingCount - 1);
      state.loading = state.pendingCount > 0;
    };
    // Only merge a real object: spreading a null/undefined payload left the
    // previous values in place while reporting success, and a non-object body
    // replaced the whole settings map.
    const mergeInto = (current: Record<string, any>, payload: unknown): Record<string, any> =>
      payload && typeof payload === 'object' && !Array.isArray(payload)
        ? { ...current, ...(payload as Record<string, any>) }
        : current;

    builder.addCase(fetchSettings.pending, handlePending)
      .addCase(fetchSettings.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.settings = (action.payload ?? {}) as Record<string, any>;
      })
      .addCase(fetchSettings.rejected, handleRejected);

    builder.addCase(updateSettings.pending, handlePending)
      .addCase(updateSettings.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.settings = mergeInto(state.settings, action.payload);
      })
      .addCase(updateSettings.rejected, handleRejected);

    builder.addCase(fetchCompanyInfo.pending, handlePending)
      .addCase(fetchCompanyInfo.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.companyInfo = (action.payload ?? {}) as Record<string, any>;
      })
      .addCase(fetchCompanyInfo.rejected, handleRejected);

    builder.addCase(updateCompanyInfo.pending, handlePending)
      .addCase(updateCompanyInfo.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.companyInfo = mergeInto(state.companyInfo, action.payload);
      })
      .addCase(updateCompanyInfo.rejected, handleRejected);

    builder.addCase(fetchPreferences.pending, handlePending)
      .addCase(fetchPreferences.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.preferences = (action.payload ?? {}) as Record<string, any>;
      })
      .addCase(fetchPreferences.rejected, handleRejected);

    builder.addCase(updatePreferences.pending, handlePending)
      .addCase(updatePreferences.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.preferences = mergeInto(state.preferences, action.payload);
      })
      .addCase(updatePreferences.rejected, handleRejected);

    builder.addCase(fetchSettingsData.pending, handlePending)
      .addCase(fetchSettingsData.fulfilled, handleFulfilled)
      .addCase(fetchSettingsData.rejected, handleRejected);

    builder.addCase(updateSettingsWithId.pending, handlePending)
      .addCase(updateSettingsWithId.fulfilled, (state, action) => {
        handleFulfilled(state);
        state.settings = mergeInto(state.settings, action.payload);
      })
      .addCase(updateSettingsWithId.rejected, handleRejected);

    builder.addCase(clearCache.pending, handlePending)
      .addCase(clearCache.fulfilled, handleFulfilled)
      .addCase(clearCache.rejected, handleRejected);
  },
});

export const { clearError, reset, setSettingLocal } = settingsSlice.actions;

export const selectSettings = (state: any) => state.settings.settings;
export const selectCompanyInfo = (state: any) => state.settings.companyInfo;
export const selectPreferences = (state: any) => state.settings.preferences;
export const selectSettingsLoading = (state: any) => state.settings.loading;
export const selectSettingsError = (state: any) => state.settings.error;
export const selectSettingByKey = (state: any, key: string, defaultValue: any = null) =>
  // `settings.settings` is `{}` before fetchSettings resolves and the slice can
  // be absent entirely in tests/partial stores, so the nested read needs a
  // guard rather than dereferencing undefined.
  state?.settings?.settings?.[key] ?? defaultValue;

export default settingsSlice.reducer;
